import { KeyValueDefault } from "@/main/models/common";
import { QuoteHeaderAnswerInformationResponse, QuoteSupplierItemPartResponse } from "@/main/models/response";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { isEmpty, isGuidEmpty, isNullUndefined } from "@/main/services/utils";
import { store } from "@/main/store";
import { IFormValueRowRoot, IRequiredFields } from "./interfaceCreateAnswer";
import { InitialFormStateType } from "./components/modalNotQuoteSubmit/modalNotQuoteSubmit";
import { QuoteSupplierItemPartRequest } from "@/main/models/request";
import { Guid } from "guid-typescript";
import { currencyToNumber } from "@/main/services/utilsNumber";
import { FieldValues } from "node_modules/react-hook-form/dist/types/fields";
import { Merge } from "node_modules/react-hook-form/dist/types/utils";
import { FieldError, FieldErrorsImpl } from "node_modules/react-hook-form/dist/types/errors";
import {
	IFormValueRow,
	pendingItemSchema,
} from "@/main/screens/quotation/createAnswer/answeredParts/createAnswerSchema";

export function averageColumnsPerRow(arrayList: object[]): number {
	let totalColumns = 0;
	const numberOfRows = arrayList.length;

	arrayList.forEach((row) => {
		totalColumns += Object.keys(row).length;
	});

	const averageColumns = totalColumns / numberOfRows;

	return averageColumns;
}

export function hasSameNumberOfCollumnsPerRow(objectList: object[]): boolean {
	if (objectList.length === 0) return true;

	const numberOfProperties = Object.keys(objectList[0]).length;

	return objectList.every((obj) => Object.keys(obj).length === numberOfProperties);
}

interface IResponseGetValidsFormValues {
	valids: IFormValueRowRoot;
	invalids: IFormValueRowRoot;
}
export function getValidFormValues(data: IFormValueRowRoot): IResponseGetValidsFormValues {
	const dataArray = Object.entries(data);

	if (dataArray.length === 0) {
		return {
			invalids: {},
			valids: {},
		};
	}
	const requiredFields: IRequiredFields[] = ["answeredPrice"];

	const validFormValues = dataArray.filter(([, value]) => {
		return value.notQuote || requiredFields.every((field) => value[field]);
	});
	const invalids = dataArray.filter(([, value]) => {
		return !value.notQuote && !requiredFields.every((field) => value[field]);
	});

	return {
		invalids: Object.fromEntries(invalids),
		valids: Object.fromEntries(validFormValues),
	};
}

export function getOnlyFormValuesPending(data: IFormValueRowRoot): IFormValueRowRoot {
	const dataObject = Object.entries(data);

	if (dataObject.length === 0) return {};

	const onlyPending = dataObject.filter(([, value]) => value.isPendingAnswer);

	return Object.fromEntries(onlyPending);
}

export function checkHasChanged(formValues: IFormValueRowRoot, dataAPi: IFormValueRowRoot) {
	const formValuesArray = Object.values(formValues);
	const dataAPiArray = Object.values(dataAPi);

	if (formValuesArray.length !== dataAPiArray.length) {
		return { changed: true, changes: null };
	}

	const dataApiMap = new Map(dataAPiArray.map((item) => [item.idUnique, item]));

	const changes: Record<string, Record<string, { oldValue: any; newValue: any }>> = {};

	formValuesArray.forEach((formValue) => {
		const dataApi = dataApiMap.get(formValue.idUnique);
		if (!dataApi) {
			changes[formValue.idUnique] = {
				itemStatus: { oldValue: "N/A", newValue: "N/A" },
			};
			return;
		}

		const changedFields: Record<string, { oldValue: any; newValue: any }> = {};
		Object.entries(formValue).forEach(([key, value]) => {
			const dataApiValue = dataApi[key as IRequiredFields];

			if (value == null || dataApiValue == null) {
				if (value !== dataApiValue) {
					changedFields[key] = {
						oldValue: dataApiValue,
						newValue: value,
					};
				}
			} else {
				if (value.toString() !== dataApiValue.toString()) {
					changedFields[key] = {
						oldValue: dataApiValue,
						newValue: value,
					};
				}
			}
		});

		if (Object.keys(changedFields).length > 0) {
			changes[formValue.idUnique] = changedFields;
		}
	});

	const hasChanged = Object.keys(changes).length > 0;

	return { changed: hasChanged, changes: hasChanged ? changes : null };
}
export function formatResponseToForm(
	item: QuoteSupplierItemPartResponse,
	isPendingAnswer: boolean,
	hasOnlyOneWarehouse?: boolean,
) {
	let formatReason: KeyValueDefault | null = null;
	if (item.guidReason && item.reason) {
		formatReason = {
			key: item.guidReason,
			value: item.reason,
			default: item.reasonDescription ? "1" : "0",
		};
	}

	const result: IFormValueRow = {
		...item,
		guidReason: item.guidReason,
		idQuoteItem: item.idQuoteItem,
		idUnique: item.idUnique,
		pickupTime: Number(item.pickupTime) > 0 ? item.pickupTime : "",
		weight: item.weight > 0 ? String(item.weight) : "",
		warehouseName: isGuidEmpty(item.guidWarehouse) ? null : { key: item.guidWarehouse, value: item.warehouseName },
		packageTypeName: item.guidPackageType ? { key: item.guidPackageType, value: item.packageTypeName } : null,
		height: isEmpty(item.height) ? "" : item.height,
		width: isEmpty(item.width) ? "" : item.width,
		depth: isEmpty(item.depth) ? "" : item.depth,
		partBrandName:
			!isGuidEmpty(item.guidBrand) && item.partBrandName && item.idPartType !== PartTypeEnum.OEM
				? { key: item.guidBrand, value: item.partBrandName }
				: null,
		reason: formatReason,
		observation: isEmpty(item.reasonDescription) ? "" : item.reasonDescription,
		backOrder: Boolean(item.backOrder),
		isPendingAnswer: isPendingAnswer,
		answeredPrice: isEmpty(item.answeredPrice) ? "" : item.answeredPrice,
		partNumber: isEmpty(item.partNumber) ? "" : item.partNumber,
		stock: isEmpty(item.stock) ? "0" : String(item.stock),
		isGroupHidden: false,
		validateWhenOnlyOneWarehouse: hasOnlyOneWarehouse || null,
		isNagsLoading: false,
		isNagsLocked: Boolean(item.isPriceFromIntegration),
		nagsPrice: item.isPriceFromIntegration ? item.answeredPrice : null,
	};

	return result;
}
function formatResponseToFormValidationInternal(item: QuoteSupplierItemPartResponse) {
	const warehouseReducerData = store.getState().warehouse.warehouse.data;
	const formattedResponse = formatResponseToForm(item, true);
	const warehouseValue =
		!isNullUndefined(warehouseReducerData) && warehouseReducerData.length === 1 ? warehouseReducerData[0] : null;
	const result = Object.assign({}, formattedResponse, {
		warehouseName: warehouseValue ? { key: warehouseValue.key, value: warehouseValue.value } : null,
	} as IFormValueRow);

	return result;
}
export function formatResponseToFormValidation(dataGridPending: QuoteSupplierItemPartResponse[]): IFormValueRowRoot {
	const resultAPIPending = dataGridPending.reduce(
		(acc, item) => ({
			...acc,
			[item.idUnique]: formatResponseToFormValidationInternal(item),
		}),
		{},
	);

	return resultAPIPending;
}

export function setNotQuoteToAllInvalidFormValues(
	formValues: IFormValueRowRoot,
	reasonProp: InitialFormStateType,
): IFormValueRowRoot {
	const { reason, observation } = reasonProp;
	const validFormValues = getValidFormValues(formValues);

	const invalids = validFormValues.invalids;
	const valids = validFormValues.valids;

	const invalidsKeys = Object.keys(invalids);

	const result = invalidsKeys.reduce((acc, key) => {
		const item = invalids[key];
		const newItem = {
			...item,
			notQuote: true,
			backOrder: false,
			reason,
			observation,
		};
		return {
			...acc,
			[key]: newItem,
		};
	}, {} as IFormValueRowRoot);

	return {
		...result,
		...valids,
	};
}

interface IGenerateRequestSaveCreateAnswerProps {
	data: IFormValueRowRoot;
	dataGridPending: QuoteSupplierItemPartResponse[];
	idQuoteSuppItemStatus: number;
	guidAddress: string;
	isEditAnswered?: boolean;
}

export function generateRequestSaveCreateAnswer({
	data,
	dataGridPending,
	guidAddress,
	idQuoteSuppItemStatus,
	isEditAnswered = false,
}: IGenerateRequestSaveCreateAnswerProps): QuoteSupplierItemPartRequest[] {
	const mappedData = Object.entries(data).map(([key, value]) => ({
		key,
		...value,
	}));
	const dataPedingToAnswer = mappedData;
	type IDataPedingToAnswer = (typeof dataPedingToAnswer)[0];

	function formatRequestMap(item: IDataPedingToAnswer): QuoteSupplierItemPartRequest {
		const weightFormatted = typeof item?.weight === "string" ? item?.weight.replace(",", ".") : item?.weight;
		if (item.notQuote) {
			const findedData = dataGridPending.find((data) => data.idUnique === item.idUnique);

			return {
				guidQuoteSupplierItem: item.guidQuoteSupplierItem ?? undefined,
				idPartType: item.idPartType,
				idQuoteSuppItemStatus: idQuoteSuppItemStatus,
				notQuote: 1,
				guidAddress: guidAddress || Guid.EMPTY,
				guidBrand: findedData?.guidBrand || Guid.EMPTY,
				guidWarehouse: findedData?.guidWarehouse || Guid.EMPTY,
				answeredPrice: findedData?.answeredPrice || 0,
				guidPackageType: findedData?.guidPackageType || Guid.EMPTY,
				partNumber: findedData?.partNumber || "",
				pickupTime: findedData?.pickupTime || 0,
				height: findedData?.height || 0,
				width: findedData?.width || 0,
				depth: findedData?.depth || 0,
				price: findedData?.price || 0,
				stock: findedData?.stock || 0,
				weight: findedData?.weight || 0,
				guidQuoteItem: findedData?.guidQuoteItem,
				guidQuoteSupplier: findedData?.guidQuoteSupplier,
				guidReason: String(item?.reason?.key) || "",
				reasonDescription: item?.observation || null,
				deliveryBySupplier: findedData?.deliveryBySupplier,
				isEditAnswered: isEditAnswered,
				isPriceFromIntegration: Boolean(findedData?.isPriceFromIntegration),
				backOrder: false,
			};
		}

		return {
			idQuoteSuppItemStatus: idQuoteSuppItemStatus,
			guidAddress: guidAddress,
			guidQuoteSupplierItem: item.guidQuoteSupplierItem ?? undefined,
			guidWarehouse: item.warehouseName?.key != null ? String(item.warehouseName.key) : undefined,
			guidBrand: item.partBrandName?.key != null ? String(item.partBrandName.key) : undefined,
			answeredPrice: currencyToNumber(String(item.answeredPrice)),
			guidPackageType: String(item.packageTypeName?.key),
			partNumber: isNullUndefined(item.partNumber) ? "" : item.partNumber,
			pickupTime: Number(item.pickupTime),
			price: Number(item.price),
			stock: Number(item.stock),
			notQuote: item.notQuote ? 1 : 0,
			height: item.height,
			width: item.width,
			depth: item.depth,
			weight: Number(weightFormatted),
			idPartType: item.idPartType,
			partTypeName: item.partTypeName,
			guidQuoteItem: item.guidQuoteItem,
			guidQuoteSupplier: item.guidQuoteSupplier,
			deliveryBySupplier: item.deliveryBySupplier,
			isEditAnswered: isEditAnswered,
			isPriceFromIntegration: Boolean(item.isNagsLocked),
			backOrder: Boolean(item.backOrder),
		};
	}

	const formattedRequest: QuoteSupplierItemPartRequest[] = dataPedingToAnswer.map(formatRequestMap);

	return formattedRequest;
}

export const isFinishedTimeWithAnswered = (dataHeader: QuoteHeaderAnswerInformationResponse) => {
	if (dataHeader.deadlineAnswer) {
		const now = new Date();
		const deadline = new Date(dataHeader.deadlineAnswer);
		const nowInUtc = new Date(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			now.getUTCHours(),
			now.getUTCMinutes(),
			now.getUTCSeconds(),
		);
		const timeDifference = deadline.getTime() - nowInUtc.getTime();

		if (timeDifference <= 0) {
			return true;
		}
		return false;
	}
};

export interface IUncoveredPart {
	label: string;
	highlightIndexes: number[];
}

export interface IPartCoverage {
	uncoveredParts: IUncoveredPart[];
	leftoverIndexes: number[];
}

function getPartGroupKey(item: IFormValueRow): string {
	return `${item.guidQuoteItem}::${item.guidQuoteSupplier}::${item.quoteProcurementPartInfo?.quoteItemPartNumber ?? ""}`;
}

function getPartLabel(item: IFormValueRow): string {
	const info = item.quoteProcurementPartInfo;
	return info?.quoteItemPartNumber || info?.quoteItemDescription || info?.partName || "";
}

export function isPartTypeAnswered(item: IFormValueRow): boolean {
	return !item.isGroupHidden && !item.notQuote && Boolean(item.answeredPrice);
}

export function getPartCoverage(items: IFormValueRow[]): IPartCoverage {
	const groups = new Map<string, number[]>();
	items.forEach((item, index) => {
		const key = getPartGroupKey(item);
		groups.set(key, [...(groups.get(key) ?? []), index]);
	});

	const uncoveredParts: IUncoveredPart[] = [];
	const leftoverIndexes: number[] = [];

	groups.forEach((indexes) => {
		if (indexes.some((index) => isPartTypeAnswered(items[index]))) {
			leftoverIndexes.push(...indexes.filter((index) => !items[index].notQuote && !isPartTypeAnswered(items[index])));
			return;
		}
		const visibleIndexes = indexes.filter((index) => !items[index].isGroupHidden);
		uncoveredParts.push({
			label: getPartLabel(items[indexes[0]]),
			highlightIndexes: visibleIndexes.length > 0 ? visibleIndexes : [indexes[0]],
		});
	});

	return { uncoveredParts, leftoverIndexes };
}

export function formatUncoveredPartsMessage(template: string, uncoveredParts: IUncoveredPart[]): string {
	return template.replace("{0}", uncoveredParts.map((part) => part.label).join(", "));
}

export function getRequiredFieldNames(item: IFormValueRow, isDelivery: boolean): string[] {
	const parsed = pendingItemSchema(isDelivery).safeParse({
		...item,
		isGroupHidden: false,
		notQuote: false,
		validateWhenOnlyOneWarehouse: null,
	});
	if (parsed.success) return [];

	const requiredFields = parsed.error.issues
		.filter((issue) => issue.code === "custom")
		.map((issue) => String(issue.path[0]));
	return Array.from(new Set(requiredFields));
}

export function getFieldArrayErrors<T extends FieldValues>(
	error: Merge<FieldError, (Merge<FieldError, FieldErrorsImpl<T>> | undefined)[]> | undefined,
): Merge<FieldError, FieldErrorsImpl<T>>[] {
	return Array.isArray(error) ? error : [];
}
